import React, { useEffect } from 'react'
import RadioGroup from '../../elements/form/RadioGroup'
import { useState } from 'react'
import ModalActionButton from '../../elements/ModalActionButton'
import { useDispatch, useSelector } from 'react-redux'
import { fetchStudents } from '../../../store/actions/studentsActions'
import NumberField from '../../elements/form/NumberField'
import ArrowIcon from '../../elements/icons/ArrowIcon'
import CloseIcon from '../../elements/icons/CloseIcon'
import CheckIcon from '../../elements/icons/CheckIcon'
import ApplicantTableSnippet from '../applications/ApplicantTableSnippet'
import SearchField from '../../elements/SearchField'
import { authHeader, debounce, baseUrl } from '../../../utils'
import { ERROR, GET_STUDENTS, SET_SUCCESS } from '../../../store/types'
import Loader from '../../elements/Loader'
import EmptyState from '../../elements/EmptyState'
import axios from 'axios'

const BlockStudentsSelection = ({closeFunction, houseDetails, block, reload}) => {
    const dispatch = useDispatch()
    const studentsSelector = useSelector(state => state.students)
    const selectionModes = [
        {
            label: "Randomize",
            description: "Students of the same house that have not been assigned a block will be randomly selected for this block according to selection rules",
            value: 'randomize'
        },
        {
            label: "Manual Selection",
            description: "Manually select from students of the current house who have not been assigned a block yet.",
            value: 'manual'
        }
    ]

    const [selectionMode, setSelectionMode] = useState('');
    // const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        if(selectionMode === 'manual') {
            dispatch(fetchStudents(`house=${houseDetails._id}&noHouseBlock=true`, 0, 0))
        }
        return () => {
            
        };
    }, [houseDetails._id, dispatch, selectionMode]);

    const [randomizationStrictness, setRandomizationStrictness] = useState({
        genderBalance: houseDetails?.gender !== 'mixed' ? 10 : 0,
        siblingSeparation: 10,
        alphabeticalSpread: 10,
        maxCapacity: 10,
    });

    const [processing, setProcessing] = useState(false);
    const [assignmentIncomplete, setAssignmentIncomplete] = useState(null);

    const randomizeStudents = async () => {
        try {
            const payload = {
                houseId: houseDetails._id,
                houseBlockId: block._id,
                strictness: randomizationStrictness,
                dryRun: false
            }

            const headers = authHeader()
            const response = await axios.post(`${baseUrl}/student-housing/house-blocks/randomize-students`, payload, { headers })

            reload()
            console.log(response.data.data)
            if(response.data.data.unassigned > 0){
                setAssignmentIncomplete(response.data.data)
            } else {
                dispatch({
                    type: SET_SUCCESS,
                    payload: `Students successfully randomized for ${houseDetails.name} - Block ${block.label}`
                })
                closeFunction()
            }
            setProcessing(false)
        } catch (error) {
            console.log(error)
            dispatch({
                type: ERROR,
                error
            })
            setProcessing(false)
        }
    }

    const pushStudentSelection = async () => {
        try {
            const payload = {
                houseId: houseDetails._id,
                houseBlockId: block._id,
                students: selectedStudents.map(student => student._id)
            }

            const headers = authHeader()

            await axios.post(`${baseUrl}/student-housing/house-blocks/assign-students`, payload, { headers })
            reload()
            dispatch({
                type: SET_SUCCESS,
                payload: `Selected students successfully assigned to ${houseDetails.name} - Block ${block.label}`
            })
            setProcessing(false)
            closeFunction()
        } catch (error) {
            console.log(error)
            dispatch({
                type: ERROR,
                error
            })
            setProcessing(false)
        }
    }

    const [activeStep, setActiveStep] = useState(1);

    const searchStudents = debounce((term) => {
        if(!term || term === '') {
            dispatch({
                type: GET_STUDENTS,
                payload: []
            })
        }
        dispatch(fetchStudents(`searchTerm=${term}`, 0, 0))
    })

    const [selectedStudents, setSelectedStudents] = useState([]);

    const toggleStudentSelect = (student) => {
        let temp = [...selectedStudents]
        if(studentSelected(student._id)) {
            temp = temp.filter(s => s._id !== student._id)
        } else {
            temp.push(student)
        }
        setSelectedStudents(temp)
    }

    const studentSelected = (studentId) => {
        const isSelected = selectedStudents.find(student => {
            return student._id === studentId
        })

        return isSelected ? true : false 
    }

    return (
        <div className='w-full'>
            {activeStep === 1 && <div className='mt-4'>
                <RadioGroup 
                    items={selectionModes} 
                    inline={false}
                    preSelectedIndex={null}
                    inputLabel={'Selection Mode'}
                    requiredField={true}
                    returnSelected={(value) => {setSelectionMode(value.value)}} 
                    hasError={false}  
                    titleField='label'
                />
            </div>}

            {activeStep === 2 && <div className='mt-4'>
                <button onClick={()=>{setActiveStep(1)}} className='flex items-center justify-center gap-x-[5px] text-gray-400 hover:text-gray-600 transition duration-200 text-sm font-[500] mb-[10px]'>
                    <ArrowIcon className={`w-4 h-4 -rotate-180`} />
                    Go back
                </button>
                {selectionMode === 'randomize' && <>
                    <h3 className='text-sm font-[550]'>Randomization strictness</h3>
                    <p className='text-sm text-gray-500'>Use the fields below to adjust the strictness of each randomization rule. use 1 for minimum strictness and 10 for maximum strictness</p>

                    {assignmentIncomplete !== null && 
                        <div className='w-full p-[10px] rounded-[8px] bg-gray-100 border-gray-500 text-gray-600'>
                            <h3 className='text-sm mb-[5px] font-[550]'>Attention!</h3>
                            <p className='text-sm mb-5'>Only {assignmentIncomplete.assigned} assigned to {houseDetails.name} - Block {block.label}.</p>
                            <p className='text-sm font-[500]'> {assignmentIncomplete.unassigned} could not be assigned because of the following rules:</p>
                            
                            {assignmentIncomplete.failedRules.map((rule, ruleIndex)=>(
                                <div key={ruleIndex} className='my-[5px] flex items-start gap-x-[5px]'>
                                    <div className='w-[5px] h-[5px] rounded-full bg-gray-400' />
                                    <p className='text-sm'>{rule}</p>
                                </div>
                            ))}
                            <p className='text-sm font-[500]'>You can relax these rules and try again.</p>
                        </div>
                    }
                    
                    {houseDetails?.gender === 'mixed' && <div className='w-full my-2'>
                        <NumberField
                            inputType="number" 
                            fieldId={`gender-balance`}
                            inputLabel="Gender Balance" 
                            requiredField={true}
                            preloadValue={randomizationStrictness.genderBalance || ''}
                            hasError={false} 
                            returnFieldValue={(value)=>{setRandomizationStrictness({...randomizationStrictness, genderBalance: value})}}
                        />
                        <p className='text-xs text-gray-400 mt-[5px]'>Sets a reasonable ratio between male and female students in a block</p>
                    </div>}

                    <div className='w-full my-2'>
                        <NumberField
                            inputType="number" 
                            fieldId={`sibling-separation`}
                            inputLabel="Sibling Separation" 
                            requiredField={true}
                            preloadValue={randomizationStrictness.siblingSeparation || ''}
                            hasError={false} 
                            returnFieldValue={(value)=>{setRandomizationStrictness({...randomizationStrictness, siblingSeparation: value})}}
                        />
                        <p className='text-xs text-gray-400 mt-[5px]'>Ensures two siblings do not end up in the same block</p>

                    </div>

                    <div className='w-full my-2'>
                        <NumberField
                            inputType="number" 
                            fieldId={`alphabetical-spread`}
                            inputLabel="Alphabetical Spread" 
                            requiredField={true}
                            preloadValue={randomizationStrictness.alphabeticalSpread || ''}
                            hasError={false} 
                            returnFieldValue={(value)=>{setRandomizationStrictness({...randomizationStrictness, alphabeticalSpread: value})}}
                        />
                        <p className='text-xs text-gray-400 mt-[5px]'>Ensures a reasonable spread of the alphabets from students surnames from A-z </p>

                    </div>

                    <div className='w-full my-2'>
                        <NumberField
                            inputType="number" 
                            fieldId={`max-capacity`}
                            inputLabel="Max Capacity" 
                            requiredField={true}
                            preloadValue={randomizationStrictness.maxCapacity || ''}
                            hasError={false} 
                            returnFieldValue={(value)=>{setRandomizationStrictness({...randomizationStrictness, maxCapacity: value})}}
                        />
                        <p className='text-xs text-gray-400 mt-[5px]'>Ensures the number of students in a block does not exceed the max capacity ({block.maxStudentCount})</p>

                    </div>
                </>}

                {selectionMode === 'manual' && <>
                    <h3 className='text-sm font-[550]'>Manual Selection</h3>
                    <p className='text-sm text-gray-500'>Search for one or more students below to add them to the block</p>

                    <div className='w-full mt-[20px] mb-[10px]'>
                        <SearchField 
                            placeholderText={`Search by student name or admission number`}
                            showResultsDropDown={false}
                            triggerSearch={(term)=>{searchStudents(term)}}
                            performingSearch={studentsSelector.loadingStudents}
                        />
                    </div>
                    {studentsSelector.loadingStudents ? 
                        <Loader />
                    :
                    <div className='overflow-y-scroll scrollbar-hidden max-h-[550px]'>
                        {studentsSelector?.students?.students?.length > 0 ? <div className='mt-[10px] w-full'>
                            {studentsSelector?.students?.students?.map((student, studentIndex) => (
                                <div key={studentIndex} className='flex items-start justify-between my-[10px]'>
                                    <ApplicantTableSnippet 
                                        applicant={student} 
                                        showAdmissionNumber={true} 
                                        imageHeightClass='h-[35px]'
                                        imageWidthClass='w-[35px]'
                                    />
                                    <button onClick={()=>{toggleStudentSelect(student)}} className={`
                                        p-[2px] rounded transition duration-200 ${studentSelected(student._id) ? 
                                        'bg-gray-600 text-white hover:text-gray-300 active:bg-black' 
                                        : 
                                        'bg-gray-100 text-gray-400  active:bg-white hover:text-gray-600'}
                                    `}>
                                        {studentSelected(student._id) ? 
                                            <CloseIcon className="w-5 h-5" /> 
                                            :
                                            <CheckIcon className="w-5 h-5" />
                                        }
                                    </button>
                                </div>
                            ))}
                        </div>
                        :
                        <div className='mt-[10px]'>
                            <EmptyState emptyStateText={`No students found matching your search`} />
                        </div>
                        }
                    </div>
                    }
                </>}
            </div>}

            <div className="mt-8 flex flex-row-reverse gap-x-4">
                {activeStep === 2 && <ModalActionButton
                    buttonLabel={`Assign students to Block ${block.label}`}
                    buttonAction={()=>{
                        if(selectionMode === 'randomize'){
                            randomizeStudents()
                        } else {
                            pushStudentSelection()
                        }
                    }}
                    processing={processing}
                />}
                {activeStep === 1 && <ModalActionButton
                    buttonLabel={`Proceed`}
                    buttonAction={()=>{
                        if(selectionMode === '') return
                        setActiveStep(2)
                    }}
                    disabled={selectionMode === ''}
                    processing={false}
                />}

                <button
                    type="button"
                    className="inline-flex justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-md hover:bg-opacity-50 hover:bg-secondary focus:outline-none"
                    onClick={closeFunction}
                >
                    Cancel
                </button>
                
            </div>
        </div>
    )
}

export default BlockStudentsSelection
